// src/features/statistics/hooks/useAudienceDemographics.ts

import { useEffect, useRef, useState } from 'react';

import { accountInsightsAPI } from '../../../shared/api/account-insights.api';

import type { InsightBreakdownResult } from '../../../shared/api/account-insights.api';
import type { AgeGroup, AudienceTimeframe, GenderData, HourlyActivity, LocationItem } from '../types/audience';

type State = {
  gender: GenderData | null;
  ageGroups: AgeGroup[];
  topCities: LocationItem[];
  topCountries: LocationItem[];
  hourlyActivity: HourlyActivity[];
  isLoading: boolean;
  error: Error | null;
};

const INITIAL_STATE: State = {
  gender: null,
  ageGroups: [],
  topCities: [],
  topCountries: [],
  hourlyActivity: [],
  isLoading: true,
  error: null,
};

function parseGender(results: InsightBreakdownResult[]): GenderData {
  let female = 0, male = 0, unknown = 0;
  for (const r of results) {
    const key = r.dimension_values[0];
    if (key === 'F') female = r.value;
    else if (key === 'M') male = r.value;
    else unknown += r.value;
  }
  const knownTotal = female + male || 1;
  const allTotal = female + male + unknown || 1;
  return {
    female: parseFloat(((female / knownTotal) * 100).toFixed(2)),
    male: parseFloat(((male / knownTotal) * 100).toFixed(2)),
    unknown: parseFloat(((unknown / allTotal) * 100).toFixed(2)),
  };
}

function parseAgeGender(results: InsightBreakdownResult[]): AgeGroup[] {
  // dimension_keys: ['age', 'gender'] — dimension_values: ['18-24', 'F']
  const map: Record<string, { all: number; male: number; female: number }> = {};
  for (const r of results) {
    const [range, gender] = r.dimension_values;
    if (!map[range]) map[range] = { all: 0, male: 0, female: 0 };
    map[range].all += r.value;
    if (gender === 'M') map[range].male += r.value;
    else if (gender === 'F') map[range].female += r.value;
  }

  const AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
  const totalAll = Object.values(map).reduce((s, v) => s + v.all, 0) || 1;
  const totalMale = Object.values(map).reduce((s, v) => s + v.male, 0) || 1;
  const totalFemale = Object.values(map).reduce((s, v) => s + v.female, 0) || 1;

  return AGE_ORDER.filter(r => map[r]).map(range => ({
    range,
    all: parseFloat(((map[range].all / totalAll) * 100).toFixed(2)),
    male: parseFloat(((map[range].male / totalMale) * 100).toFixed(2)),
    female: parseFloat(((map[range].female / totalFemale) * 100).toFixed(2)),
  }));
}

const CITY_NAME_MAP: Record<string, string> = {
  'Tselinograd': 'Astana',
  'Akmola': 'Astana',
  'Nur-Sultan': 'Astana',
  'Alma-Ata': 'Almaty',
};

export function parseLocation(results: InsightBreakdownResult[], total?: number | null): LocationItem[] {
  const denominator = total ?? (results.reduce((s, r) => s + r.value, 0) || 1);
  const sorted = [...results].sort((a, b) => b.value - a.value).slice(0, 10);
  return sorted.map(r => {
    const raw = r.dimension_values[0].split(',')[0].trim();
    return {
      name: CITY_NAME_MAP[raw] ?? raw,
      value: parseFloat(((r.value / denominator) * 100).toFixed(2)),
    };
  });
}

function parseHourlyActivity(values: Array<{ value: Record<string, number> | number; end_time: string }>): HourlyActivity[] {
  // API returns hours in account timezone (local time) — no offset needed
  const totals: Record<number, number> = {};
  let dayCount = 0;
  for (const v of values) {
    if (typeof v.value === 'object' && v.value !== null) {
      dayCount++;
      for (const [hour, count] of Object.entries(v.value)) {
        const h = parseInt(hour);
        totals[h] = (totals[h] ?? 0) + count;
      }
    }
  }
  if (dayCount === 0) return [];
  return Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    value: Math.round((totals[i] ?? 0) / dayCount),
  }));
}

export function useAudienceDemographics(
  accountId: string,
  timeframe: AudienceTimeframe,
  metric: 'follower_demographics' | 'reached_audience_demographics'
): State {
  const [state, setState] = useState<State>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!accountId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const demographicParams = { period: 'lifetime' as const, metricType: 'total_value' as const, timeframe };

    Promise.all([
      accountInsightsAPI.getAccountInsights(accountId, {
        ...demographicParams,
        metric,
        breakdown: 'gender',
      }),
      accountInsightsAPI.getAccountInsights(accountId, {
        ...demographicParams,
        metric,
        breakdown: 'age,gender',
      }),
      accountInsightsAPI.getAccountInsights(accountId, {
        ...demographicParams,
        metric,
        breakdown: 'city',
      }),
      accountInsightsAPI.getAccountInsights(accountId, {
        ...demographicParams,
        metric,
        breakdown: 'country',
      }),
      accountInsightsAPI.getAccountInsights(accountId, {
        metric: 'online_followers',
        period: 'lifetime',
        metricType: 'time_series',
      }),
    ])
      .then(([genderRes, ageRes, cityRes, countryRes, activityRes]) => {
        if (controller.signal.aborted) return;

        const genderResults = genderRes.data[0]?.total_value?.breakdowns?.[0]?.results ?? [];
        const ageResults = ageRes.data[0]?.total_value?.breakdowns?.[0]?.results ?? [];
        const cityResults = cityRes.data[0]?.total_value?.breakdowns?.[0]?.results ?? [];
        const countryResults = countryRes.data[0]?.total_value?.breakdowns?.[0]?.results ?? [];
        const activityValues = activityRes.data[0]?.values ?? [];

        const genderTotal = genderResults.length > 0
          ? genderResults.reduce((s, r) => s + r.value, 0)
          : null;

        setState({
          gender: genderResults.length > 0 ? parseGender(genderResults) : null,
          ageGroups: parseAgeGender(ageResults),
          topCities: parseLocation(cityResults, genderTotal),
          topCountries: parseLocation(countryResults, genderTotal),
          hourlyActivity: parseHourlyActivity(activityValues),
          isLoading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        }));
      });

    return () => { controller.abort(); };
  }, [accountId, timeframe, metric]);

  return state;
}
